import { useEffect, useState, useCallback } from 'react';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import { dataGridSx } from '../utils/dataGridStyles';
import LoadingOverlay from '../components/LoadingOverlay';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, Chip, IconButton, Tooltip,
  Divider, Stack, Autocomplete, CircularProgress, Skeleton
} from '@mui/material';
import {
  DataGrid, GridColDef, GridRowModes, GridRowModesModel,
  GridActionsCellItem, GridRowModel
} from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

interface Contract {
  contract_id: number;
  contract_number: string;
  contract_name: string;
  contract_type: string;
  project_id: number;
  project_name?: string;
  original_value: number;
  total_value?: number;
  remaining_balance?: number;
  boq_value?: number;
  currency?: string;
  status: string;
  budget: number | null;
  rejection_comments: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_by: string;
  created_at: string;
  modified_by: string | null;
  updated_at: string | null;
}

interface BOQLine {
  line_id: number;
  contract_id: number;
  line_no: number;
  description: string;
  qty: number;
  uom: string;
  unit_price: number;
  line_total: number;
  total_cost?: number; // For compatibility
  pm_comment: string | null;
  isNew?: boolean;
}

interface RateSheetItem {
  rsi_id: number;
  bli_id: number;
  item_code: string;
  item_description: string;
  unit_code: string;
  unit_price: number;
  category_name: string;
}

const ContractVOApproval = ({ projectId, access = 'none', userId, projects = [] }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [boqLines, setBoqLines] = useState<BOQLine[]>([]);
  const [boqRowModesModel, setBoqRowModesModel] = useState<GridRowModesModel>({});
  const [linkedDCs, setLinkedDCs] = useState<any[]>([]);
  const [rateSheetItems, setRateSheetItems] = useState<RateSheetItem[]>([]);
  const [budgetLineItems, setBudgetLineItems] = useState<RateSheetItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [budget, setBudget] = useState<string>('');
  const [rejectionComments, setRejectionComments] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Company data for number formatting and header
  const [companyData, setCompanyData] = useState({
    number_format: '1,234.56',
    company_name: '',
    logo_url: '',
    address: '',
    phone: '',
    email: ''
  });

  const canRead = access === 'read' || access === 'write' || access === 'admin';
  const canWrite = access === 'write' || access === 'admin';

  // Fetch contracts (all statuses except Rejected)
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const url = projectId
        ? apiUrl(`/api/contracts?project_id=${projectId}&include_lines=true`)
        : apiUrl('/api/contracts?include_lines=true');

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contracts');
      }

      const data = await response.json();
      console.log('📊 Contracts fetched:', data);
      // Only show Pending Approval and Approved contracts
      const filteredData = Array.isArray(data) ? data.filter(c => c.status === 'Pending Approval' || c.status === 'Approved') : [];
      setContracts(filteredData);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load contracts',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (canRead) {
      fetchContracts();
    }
  }, [canRead, fetchContracts]);

  // Fetch company data for number formatting
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), {
          credentials: 'include'
        });
        if (!projectRes.ok) return;
        const currentProject = await projectRes.json();
        if (!currentProject.company_id) return;

        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), {
          credentials: 'include'
        });
        if (!response.ok) return;
        const data = await response.json();
        setCompanyData({
          number_format: data.number_format || '1,234.56',
          company_name: data.company_name || '',
          logo_url: data.logo_url || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || ''
        });
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
    if (projectId) fetchCompanyData();
  }, [projectId]);

  // Fetch Budget Line Items for the project and enrich with prices from rate sheet
  useEffect(() => {
    if (projectId) {
      // Fetch all budget line items first
      fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          return res.json();
        })
        .then(budgetItems => {
          const items = Array.isArray(budgetItems) ? budgetItems : [];
          console.log('💰 Budget Line Items fetched:', items.length, 'items');

          // Now try to fetch rate sheet items to get current prices
          fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
            .then(res => res.json())
            .then(rateSheets => {
              const activeSheet = Array.isArray(rateSheets)
                ? rateSheets.find(rs => rs.is_active) || rateSheets[0]
                : null;

              if (activeSheet) {
                console.log('🔍 Active rate sheet found:', activeSheet.rate_sheet_id);
                // Fetch rate sheet items to get prices
                fetch(apiUrl(`/api/rate-sheets/${activeSheet.rate_sheet_id}/items`))
                  .then(res => res.json())
                  .then(rateItems => {
                    const rateItemsArray = Array.isArray(rateItems) ? rateItems : [];
                    console.log('📋 Rate sheet items fetched for enrichment:', rateItemsArray.length);

                    // Merge: for each budget item, add unit_price from rate sheet if available
                    const enrichedItems = items.map(item => {
                      const rateItem = rateItemsArray.find(ri => ri.bli_id === item.bli_id);
                      return {
                        ...item,
                        unit_price: rateItem ? rateItem.unit_price : 0
                      };
                    });

                    console.log('✅ Enriched budget items with prices:', enrichedItems.length);
                    console.log('✅ First enriched item:', enrichedItems[0]);
                    setBudgetLineItems(enrichedItems);
                  })
                  .catch(() => {
                    // If rate sheet items fetch fails, use budget items with 0 price
                    console.log('⚠️ Failed to fetch rate sheet items, using budget items with 0 price');
                    setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
                  });
              } else {
                // No rate sheet, use budget items with 0 price
                console.log('⚠️ No active rate sheet found, using budget items with 0 price');
                setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
              }
            })
            .catch(() => {
              // If rate sheet fetch fails, use budget items with 0 price
              console.log('⚠️ Failed to fetch rate sheets, using budget items with 0 price');
              setBudgetLineItems(items.map(item => ({ ...item, unit_price: 0 })));
            });
        })
        .catch(err => {
          console.error('Failed to fetch budget line items:', err);
          setBudgetLineItems([]);
        });
    }
  }, [projectId, access]);

  // Fetch rate sheet items when a contract is selected
  const fetchRateSheetItems = useCallback(async (rateSheetId: number | null) => {
    if (!rateSheetId) {
      console.log('⚠️ No rate sheet ID, clearing rate sheet items');
      setRateSheetItems([]);
      return;
    }

    try {
      console.log('🔄 Fetching rate sheet items for rate sheet:', rateSheetId);
      const response = await fetch(apiUrl(`/api/rate-sheets/${rateSheetId}/items`), {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📋 Rate Sheet Items fetched:', data.length, 'items');
      console.log('📋 First item:', data[0]);
      setRateSheetItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Failed to fetch rate sheet items:', err);
      setRateSheetItems([]);
    }
  }, []);

  // Fetch BOQ lines for selected contract
  const fetchBOQLines = useCallback(async (contractId: number) => {
    try {
      const response = await fetch(
        apiUrl(`/api/contracts/${contractId}/budget-lines`),
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch BOQ lines');
      }

      const data = await response.json();
      setBoqLines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching BOQ lines:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load BOQ lines',
        severity: 'error'
      });
    }
  }, []);

  // Handle view contract details
  const handleViewContract = useCallback(async (contract: Contract) => {
    setSelectedContract(contract);
    setBudget(contract.budget?.toString() || contract.original_value?.toString() || '');
    setRejectionComments('');
    setLinkedDCs([]);
    setDialogOpen(true);
    setDialogLoading(true);

    try {
      // Fetch rate sheet items if contract has a rate sheet
      const contractData = contract as any; // Type assertion to access rate_sheet_id
      if (contractData.rate_sheet_id) {
        await fetchRateSheetItems(contractData.rate_sheet_id);
      } else {
        setRateSheetItems([]);
      }

      await fetchBOQLines(contract.contract_id);

      // Fetch linked design changes for VOs
      if (contract.contract_type === 'variation_order') {
        fetch(apiUrl(`/api/contract-design-changes?contract_id=${contract.contract_id}`), { credentials: 'include' })
          .then(r => r.ok ? r.json() : [])
          .then(data => setLinkedDCs(Array.isArray(data) ? data : []))
          .catch(() => {});
      }
    } finally {
      setDialogLoading(false);
    }
  }, [fetchBOQLines, fetchRateSheetItems]);

  // Handle BOQ row edit
  const handleBOQEditClick = useCallback((id: number) => {
    setBoqRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit }
    }));
  }, []);

  // Handle BOQ row save
  const handleBOQSaveClick = useCallback((id: number) => {
    setBoqRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View }
    }));
  }, []);

  // Handle BOQ row cancel
  const handleBOQCancelClick = useCallback((id: number) => {
    setBoqRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    }));

    const editedRow = boqLines.find((row) => row.line_id === id);
    if (editedRow?.isNew) {
      setBoqLines((oldRows) => oldRows.filter((row) => row.line_id !== id));
    }
  }, [boqLines]);

  // Handle BOQ row update
  const processBoqRowUpdate = useCallback(async (newRow: GridRowModel) => {
    const updatedRow = { ...newRow } as BOQLine;

    // Recalculate total_cost using correct field names
    updatedRow.total_cost = (newRow.qty || 0) * (newRow.unit_price || 0);

    if (newRow.isNew) {
      // Add new BOQ line
      try {
        const response = await fetch(
          apiUrl(`/api/contracts/${selectedContract?.contract_id}/budget-lines`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              line_no: newRow.line_no,
              description: newRow.description,
              quantity: newRow.qty,
              unit: newRow.uom,
              unit_rate: newRow.unit_price,
              pm_comment: newRow.pm_comment
            })
          }
        );

        if (!response.ok) {
          throw new Error('Failed to add BOQ line');
        }

        const savedLine = await response.json();
        setBoqLines((oldRows) =>
          oldRows.map((row) =>
            row.line_id === newRow.line_id ? { ...savedLine, isNew: false } : row
          )
        );

        setSnackbar({
          open: true,
          message: 'BOQ line added successfully',
          severity: 'success'
        });

        return savedLine;
      } catch (error) {
        console.error('Error adding BOQ line:', error);
        setSnackbar({
          open: true,
          message: 'Failed to add BOQ line',
          severity: 'error'
        });
        throw error;
      }
    } else {
      // Update existing BOQ line
      try {
        const response = await fetch(
          apiUrl(`/api/contracts/${selectedContract?.contract_id}/budget-lines/${newRow.line_id}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              description: newRow.description,
              quantity: newRow.qty,
              unit: newRow.uom,
              unit_rate: newRow.unit_price,
              pm_comment: newRow.pm_comment
            })
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update BOQ line');
        }

        setBoqLines((oldRows) =>
          oldRows.map((row) => (row.line_id === newRow.line_id ? updatedRow : row))
        );

        setSnackbar({
          open: true,
          message: 'BOQ line updated successfully',
          severity: 'success'
        });

        return updatedRow;
      } catch (error) {
        console.error('Error updating BOQ line:', error);
        setSnackbar({
          open: true,
          message: 'Failed to update BOQ line',
          severity: 'error'
        });
        throw error;
      }
    }
  }, [selectedContract]);

  // Handle delete BOQ line
  const handleDeleteBOQLine = useCallback(async (lineId: number) => {
    if (!window.confirm('Are you sure you want to delete this BOQ line?')) {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(`/api/contracts/${selectedContract?.contract_id}/budget-lines/${lineId}`),
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete BOQ line');
      }

      setBoqLines((oldRows) => oldRows.filter((row) => row.line_id !== lineId));

      setSnackbar({
        open: true,
        message: 'BOQ line deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting BOQ line:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete BOQ line',
        severity: 'error'
      });
    }
  }, [selectedContract]);

  // Handle add new BOQ line
  const handleAddBOQLine = useCallback(() => {
    const newId = Math.max(0, ...boqLines.map((l) => l.line_id)) + 1;
    const newLineNo = Math.max(0, ...boqLines.map((l) => l.line_no)) + 1;

    const newLine: BOQLine = {
      line_id: newId,
      contract_id: selectedContract?.contract_id || 0,
      line_no: newLineNo,
      description: '',
      qty: 1,
      uom: 'lot',
      unit_price: 0,
      line_total: 0,
      total_cost: 0,
      pm_comment: null,
      isNew: true
    };

    setBoqLines((oldRows) => [...oldRows, newLine]);
    setBoqRowModesModel((oldModel) => ({
      ...oldModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'description' }
    }));
  }, [boqLines, selectedContract]);

  // Export BOQ to Excel
  const handleExportBOQ = useCallback(() => {
    if (!selectedContract || boqLines.length === 0) {
      setSnackbar({
        open: true,
        message: 'No BOQ lines to export',
        severity: 'warning'
      });
      return;
    }

    const isApproved = selectedContract.status === 'Approved';
    const exportData = boqLines.map((line) => {
      const row: Record<string, string | number> = {
        'Line No': line.line_no,
        'Description': line.description,
        'Quantity': line.qty,
        'Unit': line.uom,
        'Unit Rate': line.unit_price,
        'Total Cost': line.line_total || (line.qty || 0) * (line.unit_price || 0),
      };
      if (!isApproved) {
        row['PM Comment'] = line.pm_comment || '';
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BOQ');

    const fileName = `${selectedContract.contract_number}_BOQ_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    setSnackbar({
      open: true,
      message: 'BOQ exported successfully',
      severity: 'success'
    });
  }, [selectedContract, boqLines]);

  // Handle approve contract
  const handleApprove = useCallback(async () => {
    if (!selectedContract) return;

    if (!budget || parseFloat(budget) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid budget amount',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await fetch(
        apiUrl(`/api/contracts/${selectedContract.contract_id}/status`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: 'Approved',
            budget: parseFloat(budget)
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve contract');
      }

      setSnackbar({
        open: true,
        message: `${selectedContract.contract_type === 'variation_order' ? 'VO' : 'Contract'} approved successfully`,
        severity: 'success'
      });

      setApproveDialogOpen(false);
      setDialogOpen(false);
      fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error: any) {
      console.error('Error approving contract:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to approve contract',
        severity: 'error'
      });
    }
  }, [selectedContract, budget, fetchContracts]);

  // Handle reject contract
  const handleReject = useCallback(async () => {
    if (!selectedContract) return;

    try {
      const response = await fetch(
        apiUrl(`/api/contracts/${selectedContract.contract_id}/status`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: 'Rejected',
            rejection_comments: rejectionComments
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reject contract');
      }

      setSnackbar({
        open: true,
        message: `${selectedContract.contract_type === 'variation_order' ? 'VO' : 'Contract'} rejected`,
        severity: 'success'
      });

      setRejectDialogOpen(false);
      setDialogOpen(false);
      fetchContracts();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error: any) {
      console.error('Error rejecting contract:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to reject contract',
        severity: 'error'
      });
    }
  }, [selectedContract, rejectionComments, fetchContracts]);

  // Contract columns
  const contractColumns: GridColDef[] = [
    {
      field: 'contract_number',
      headerName: 'Contract/VO Number',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'contract_name',
      headerName: 'Name',
      width: 250
    },
    {
      field: 'contract_type',
      headerName: 'Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value === 'variation_order' ? 'VO' : 'Contract'}
          color={params.value === 'variation_order' ? 'secondary' : 'primary'}
          size="small"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusColors: { [key: string]: 'default' | 'warning' | 'info' | 'success' | 'error' } = {
          'Draft': 'default',
          'Submitted': 'info',
          'Pending Approval': 'warning',
          'Approved': 'success',
          'Rejected': 'error'
        };
        return (
          <Tooltip title={params.value || 'N/A'}>
            <Chip
              label={params.value || 'N/A'}
              color={statusColors[params.value] || 'default'}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'project_name',
      headerName: 'Project',
      width: 200
    },
    {
      field: 'original_value',
      headerName: 'Original Contract Value',
      width: 170,
      type: 'number',
      renderCell: (params) => {
        if (params.value == null || params.value === undefined) return '-';
        return formatCurrency(parseFloat(params.value), params.row.currency || 'SGD', companyData.number_format);
      }
    },
    {
      field: 'line_total',
      headerName: 'Original BOQ Value',
      width: 170,
      type: 'number',
      renderCell: (params) => {
        const lines = params.row.lines;
        if (!lines || lines.length === 0) return <Typography>-</Typography>;
        const total = lines.reduce((sum: number, line: any) => {
          const qty = line.qty || 0;
          const price = line.unit_price || 0;
          return sum + (qty * price);
        }, 0);
        if (total === 0) return <Typography>-</Typography>;
        return <Typography>{formatCurrency(total, params.row.currency || 'SGD', companyData.number_format)}</Typography>;
      }
    },
    {
      field: 'budget',
      headerName: 'Approved Budget',
      width: 160,
      type: 'number',
      renderCell: (params) => {
        const val = params.row.budget;
        if (val == null || val === undefined) return <Typography>N/A</Typography>;
        return <Typography>{formatCurrency(val, params.row.currency || 'SGD', companyData.number_format)}</Typography>;
      }
    },
    {
      field: 'spent',
      headerName: 'Spent (MR/PO)',
      width: 150,
      type: 'number',
      renderCell: (params) => {
        const spent = (params.row.total_value || 0) - (params.row.remaining_balance || 0);
        return <Typography>{formatCurrency(spent, params.row.currency || 'SGD', companyData.number_format)}</Typography>;
      }
    },
    {
      field: 'remaining_balance',
      headerName: 'Budget Balance',
      width: 150,
      type: 'number',
      renderCell: (params) => {
        const budget = params.row.budget;
        if (budget == null) return <Typography>N/A</Typography>;
        const spent = (params.row.total_value || 0) - (params.row.remaining_balance || 0);
        return <Typography>{formatCurrency(budget - spent, params.row.currency || 'SGD', companyData.number_format)}</Typography>;
      }
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewContract(params.row)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // BOQ columns - Matching QSContractOrVO.tsx field names
  const boqColumns: GridColDef[] = [
    {
      field: 'line_no',
      headerName: 'Line No',
      width: 80,
      editable: false
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      editable: true,
      renderEditCell: (params) => {
        // Use budget line items (enriched with prices) if available, otherwise use rate sheet items
        const itemsToShow = budgetLineItems.length > 0 ? budgetLineItems : rateSheetItems;

        return (
          <Autocomplete
            fullWidth
            size="small"
            options={itemsToShow}
            getOptionLabel={(option) => option.item_description || ''}
            value={itemsToShow.find((item: RateSheetItem) => item.item_description === params.value) || null}
            noOptionsText={itemsToShow.length === 0 ? "No budget/rate items available" : "No matching items found"}
            onChange={(event, newValue) => {
              if (newValue) {
                console.log('🎯 Selected rate sheet item:', newValue);
                console.log('  - Description:', newValue.item_description);
                console.log('  - Unit Price:', newValue.unit_price);
                console.log('  - Unit Code:', newValue.unit_code);

                // Update description field
                params.api.setEditCellValue({ id: params.id, field: 'description', value: newValue.item_description || '' });

                // Update other fields with a slight delay - USING QSContractOrVO field names
                setTimeout(() => {
                  try {
                    console.log('  ✅ Setting unit_price to:', newValue.unit_price);
                    params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newValue.unit_price || 0 });
                  } catch (e) { console.error('Error setting unit_price:', e); }

                  try {
                    console.log('  ✅ Setting uom to:', newValue.unit_code);
                    params.api.setEditCellValue({ id: params.id, field: 'uom', value: newValue.unit_code || '' });
                  } catch (e) { console.error('Error setting uom:', e); }
                }, 100);
              } else {
                // If cleared, just update description
                params.api.setEditCellValue({ id: params.id, field: 'description', value: '' });
              }
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                variant="standard"
                placeholder="Search and select item..."
                InputProps={{
                  ...inputParams.InputProps,
                  disableUnderline: true,
                  sx: { fontSize: '0.875rem' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.item_description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.category_name} • {option.unit_code} • ${option.unit_price}
                  </Typography>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              return options.filter(option =>
                option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
          />
        );
      }
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 100,
      type: 'number',
      editable: true
    },
    {
      field: 'uom',
      headerName: 'UOM',
      width: 100,
      editable: true
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 130,
      type: 'number',
      editable: true
    },
    {
      field: 'line_total',
      headerName: 'Line Total',
      width: 150,
      type: 'number',
      editable: false,
      valueGetter: (value, row) => {
        if (!row) return 0;
        const qty = row.qty || 0;
        const price = row.unit_price || 0;
        return qty * price;
      },
      valueFormatter: (value) => {
        if (value == null) return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return formatCurrency(numValue, '', companyData.number_format).replace(/^[A-Z]{3}\s*/, '');
      }
    },
    {
      field: 'pm_comment',
      headerName: 'PM Comment',
      flex: 1,
      minWidth: 200,
      editable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5, py: 0.5, fontSize: '0.85rem' }}>
          {params.value || ''}
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id, row }) => {
        const isInEditMode = boqRowModesModel[id]?.mode === GridRowModes.Edit;
        const isPendingApproval = selectedContract?.status === 'Pending Approval';

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={() => handleBOQSaveClick(id as number)}
              color="primary"
              showInMenu={false}
              key="save"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CloseIcon /></Tooltip>}
              label="Cancel"
              onClick={() => handleBOQCancelClick(id as number)}
              color="inherit"
              showInMenu={false}
              key="cancel"
            />
          ];
        }

        if (!isPendingApproval) {
          return [];
        }

        return [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={() => handleBOQEditClick(id as number)}
            color="primary"
            showInMenu={false}
            key="edit"
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteIcon sx={{ color: 'error.main' }} /></Tooltip>}
            label="Delete"
            onClick={() => handleDeleteBOQLine(id as number)}
            showInMenu={false}
            key="delete"
          />
        ];
      }
    }
  ];

  const totalBOQCost = boqLines.reduce((sum, line) => {
    const lineTotal = line.line_total || (line.qty || 0) * (line.unit_price || 0);
    return sum + lineTotal;
  }, 0);

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You do not have permission to view Contract/VO Approvals.
        </Alert>
      </Box>
    );
  }

  const contractItems = contracts.filter(c => c.contract_type !== 'variation_order');
  const voItems = contracts.filter(c => c.contract_type === 'variation_order');
  const contractPending = contractItems.filter(c => c.status === 'Pending Approval').length;
  const voPending = voItems.filter(c => c.status === 'Pending Approval').length;

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <LoadingOverlay loading={loading} size={50} />

      {/* Summary Bar */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>Contracts</Typography>
          <Typography variant="body2" fontWeight={700}>
            <Box component="span" sx={{ color: contractPending > 0 ? 'error.main' : 'text.secondary' }}>{contractPending}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>/{contractItems.length}</Box>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>Variation Orders</Typography>
          <Typography variant="body2" fontWeight={700}>
            <Box component="span" sx={{ color: voPending > 0 ? 'error.main' : 'text.secondary' }}>{voPending}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>/{voItems.length}</Box>
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={contracts}
          columns={contractColumns}
          getRowId={(row) => row.contract_id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          getRowHeight={() => 'auto'}
          onRowDoubleClick={(params) => handleViewContract(params.row)}
          sx={{ ...dataGridSx, '& .MuiDataGrid-row': { ...dataGridSx['& .MuiDataGrid-row'], cursor: 'pointer' } }}
        />
      </Paper>

      {/* Review Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedContract?.status === 'Pending Approval' ? 'Review' : 'View'} {selectedContract?.contract_type === 'variation_order' ? 'VO' : 'Contract'}: {selectedContract?.contract_number}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedContract && (
            <div className="doc-preview">
              {/* Company Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
                {companyData.logo_url && (
                  <Box
                    component="img"
                    src={companyData.logo_url}
                    alt="logo"
                    sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1, border: '1px solid #eee' }}
                  />
                )}
                <Box>
                  <Typography variant="h6" fontWeight={700}>{companyData.company_name || '—'}</Typography>
                  {companyData.address && (
                    <Typography variant="body2" color="text.secondary">{companyData.address}</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    {companyData.phone && (
                      <Typography variant="body2"><strong>Tel:</strong> {companyData.phone}</Typography>
                    )}
                    {companyData.email && (
                      <Typography variant="body2"><strong>Email:</strong> {companyData.email}</Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight={800}>
                    {selectedContract.contract_type === 'variation_order' ? 'VARIATION ORDER' : 'CONTRACT'}
                  </Typography>
                  <Chip
                    label={selectedContract.status}
                    color={
                      selectedContract.status === 'Approved' ? 'success' :
                      selectedContract.status === 'Pending Approval' ? 'warning' :
                      selectedContract.status === 'Rejected' ? 'error' : 'default'
                    }
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              <Box sx={{ height: 3, background: 'rgb(148,0,0)', borderRadius: 1, mb: 2.5 }} />

              {/* Contract Info */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Contract Number</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedContract.contract_number}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Contract Name</Typography>
                  <Typography variant="body1">{selectedContract.contract_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Original Value</Typography>
                  <Typography variant="body1">
                    {formatCurrency(selectedContract.original_value, selectedContract.currency || 'SGD', companyData.number_format)}
                  </Typography>
                </Box>
                {selectedContract.currency && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Currency</Typography>
                    <Typography variant="body1">{selectedContract.currency}</Typography>
                  </Box>
                )}
                {selectedContract.project_name && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Project</Typography>
                    <Typography variant="body1">{selectedContract.project_name}</Typography>
                  </Box>
                )}
                {(selectedContract as any).signed_date && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Signed Date</Typography>
                    <Typography variant="body1">{(selectedContract as any).signed_date}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Created By</Typography>
                  <Typography variant="body1">{selectedContract.created_by}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1">
                    {new Date(selectedContract.created_at).toLocaleString()}
                  </Typography>
                </Box>
                {selectedContract.approved_by && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Approved By</Typography>
                    <Typography variant="body1">{selectedContract.approved_by}</Typography>
                  </Box>
                )}
                {selectedContract.approved_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Approved At</Typography>
                    <Typography variant="body1">
                      {new Date(selectedContract.approved_at).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Associated Design Changes — VO only */}
              {selectedContract.contract_type === 'variation_order' && linkedDCs.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                    Reference
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {linkedDCs.map((dc: any) => (
                      <Chip
                        key={dc.change_number}
                        label={dc.change_name ? `${dc.change_number} — ${dc.change_name}` : dc.change_number}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Previous Rejection Comments */}
              {selectedContract.rejection_comments && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ p: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.light', borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" color="error.dark" sx={{ fontWeight: 600, mb: 1 }}>
                      Previously Rejected — Manager Comments:
                    </Typography>
                    <Typography variant="body2" color="error.dark" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                      {selectedContract.rejection_comments}
                    </Typography>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* BOQ Section */}
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Bill of Quantities (BOQ)
                  </Typography>
                  <Button
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportBOQ}
                    variant="outlined"
                    size="small"
                  >
                    Export to Excel
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddBOQLine}
                    variant="outlined"
                    size="small"
                    disabled={!canWrite || selectedContract?.status !== 'Pending Approval'}
                  >
                    Add Line
                  </Button>
                </Stack>

                <Box sx={{ height: 400, width: '100%', mb: 2 }}>
                  <DataGrid
                    rows={boqLines}
                    columns={selectedContract?.status === 'Approved' ? boqColumns.filter(c => c.field !== 'pm_comment' && c.field !== 'actions') : boqColumns}
                    getRowId={(row) => row.line_id}
                    editMode="row"
                    rowModesModel={boqRowModesModel}
                    onRowModesModelChange={setBoqRowModesModel}
                    processRowUpdate={processBoqRowUpdate}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                    getRowHeight={() => 'auto'}
                    disableRowSelectionOnClick
                    sx={{ '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 } }}
                  />
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6">
                    Total BOQ Cost: {formatCurrency(totalBOQCost, 'SGD', companyData.number_format)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Budget Assignment */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedContract.status === 'Pending Approval' ? 'Assign Budget (Required for Approval)' : 'Budget'}
                </Typography>
                {/* Screen: editable TextField */}
                <TextField
                  fullWidth
                  type="number"
                  label="Budget"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={selectedContract.status === 'Pending Approval' ? 'The internal budget allocation for this contract/VO' : 'The approved budget for this contract/VO'}
                  disabled={selectedContract.status !== 'Pending Approval'}
                  sx={{ '@media print': { display: 'none' } }}
                />
                {/* Print-only: show Budget as plain styled row */}
                <Box sx={{ display: 'none', '@media print': { display: 'block', borderBottom: '1px solid #e0e0e0', pb: 1 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>Budget</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {budget ? formatCurrency(parseFloat(budget), selectedContract.currency || 'SGD', companyData.number_format) : '—'}
                  </Typography>
                </Box>

                {/* Profit/Loss Calculation */}
                {budget && parseFloat(budget) > 0 && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Financial Summary
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total BOQ Cost:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(totalBOQCost, 'SGD', companyData.number_format)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Budget:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(parseFloat(budget), 'SGD', companyData.number_format)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {totalBOQCost - parseFloat(budget) >= 0 ? 'Profit:' : 'Loss:'}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: totalBOQCost - parseFloat(budget) >= 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {formatCurrency(Math.abs(totalBOQCost - parseFloat(budget)), 'SGD', companyData.number_format)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Formula: Profit/Loss = Total BOQ Cost - Budget
                      </Typography>
                      <Chip
                        label={totalBOQCost - parseFloat(budget) >= 0 ? 'Profitable' : 'Loss Making'}
                        color={totalBOQCost - parseFloat(budget) >= 0 ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              const prev = document.title;
              document.title = selectedContract?.contract_type === 'variation_order'
                ? `VO-${selectedContract?.contract_number || 'draft'}`
                : `Contract-${selectedContract?.contract_number || 'draft'}`;
              window.print();
              document.title = prev;
            }}
          >
            Print / PDF
          </Button>
          {selectedContract?.status === 'Pending Approval' && (
            <>
              <Button
                onClick={() => setRejectDialogOpen(true)}
                startIcon={<CancelIcon />}
                color="error"
                variant="outlined"
              >
                Reject
              </Button>
              <Button
                onClick={() => setApproveDialogOpen(true)}
                startIcon={<CheckCircleIcon />}
                color="success"
                variant="contained"
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve {selectedContract?.contract_type === 'variation_order' ? 'VO' : 'Contract'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to approve this {selectedContract?.contract_type === 'variation_order' ? 'VO' : 'contract'}?
          </Typography>

          {budget && parseFloat(budget) > 0 && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total BOQ Cost
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalBOQCost, 'SGD', companyData.number_format)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Budget
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(parseFloat(budget), 'SGD', companyData.number_format)}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {totalBOQCost - parseFloat(budget) >= 0 ? 'Expected Profit' : 'Expected Loss'}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: totalBOQCost - parseFloat(budget) >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {formatCurrency(Math.abs(totalBOQCost - parseFloat(budget)), 'SGD', companyData.number_format)}
                  </Typography>
                  <Chip
                    label={totalBOQCost - parseFloat(budget) >= 0 ? 'Profitable' : 'Loss Making'}
                    color={totalBOQCost - parseFloat(budget) >= 0 ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleApprove} color="success" variant="contained">
            Confirm Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject {selectedContract?.contract_type === 'variation_order' ? 'VO' : 'Contract'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject this {selectedContract?.contract_type === 'variation_order' ? 'VO' : 'contract'}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Comments (Optional)"
            value={rejectionComments}
            onChange={(e) => setRejectionComments(e.target.value)}
            helperText="Provide feedback to the QS team about why this was rejected"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectionComments.trim()}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractVOApproval;
